<script setup lang="ts">
/**
 * スレッドを表すコンポーネント
 */
import { computed, nextTick, onMounted, onUnmounted, provide, ref } from 'vue';
import { onBeforeRouteUpdate, useRoute } from 'vue-router';
import api from '@/api';
import { trackingVisasqNowEvent } from '@/api/tracking';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import SurveyInput from '@/components/survey/input/survey-input.vue';
import SurveySessionSwitcherInput from '@/components/survey/input/survey-session-switcher-input.vue';
import SurveyCard from '@/components/survey/survey-card.vue';
import SurveyHeader from '@/components/survey/survey-header.vue';
import SurveyHistory from '@/components/survey/survey-history.vue';
import SurveyRelatedQuestions from '@/components/survey/survey-related-questions.vue';
import VisasqNowQuestionDraft from '@/components/survey/visasq/visasq-now-question-draft.vue';
import {
  AdpDocument,
  FilterInfo,
  ShareSurveysKind,
  SpecifiedMethod,
  SurveyExecutionFrom,
  SurveyHistoryData,
  SurveyInputValue,
  SurveyPromptTemplateTrackingInfo,
  SurveyPromptType,
  SurveySessionResponse,
  SurveyType,
  VisasqNowQuestionDraftData,
  VisasqNowQuestionDraft as VisasqNowQuestionDraftType,
} from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useSurveyExecutionStatus } from '@/utils/composables/useSurveyExecutionStatus';
import { useUserDocumentActionHistories } from '@/utils/composables/useUserDocumentActionHistories';
import { formatDate } from '@/utils/formatters';
import { specifiedMethodKey } from '@/utils/injectionKeys';
import {
  checkRequestState,
  convertKeysToCamelCase,
} from '@/utils/survey/common';
import {
  DEFAULT_SURVEY_PROGRESS_MESSAGE,
  DOC_TYPES_FOR_SCOPE,
  SESSION_LIMIT_COUNT,
  SURVEY_HISTORIES_PAGE_NAME,
} from '@/utils/survey/constants';
import SurveyGenerator from '@/utils/survey/surveyGenerator';
import {
  isDefaultSurveyQuery,
  trackSubmitSurvey,
  trackSurveyPageView,
} from '@/utils/survey/tracking';
import { useTeamInfo, useUserInfo } from '@/utils/swr';
import { userSession } from '@/utils/userSession';
import { useEmitter, useStore } from '@/utils/vue';

const emitter = useEmitter();
const route = useRoute();
const store = useStore();
const { getCurrentPathForTracking } = useCurrentPathForTracking();

type ControllerWithMode = {
  controller: AbortController | null;
  deepMode: boolean;
};

/**
 * Local States
 */

/* ヘッダー情報 */
const { data: userInfo } = useUserInfo();
const survey = ref<SurveySessionResponse>();

/* 契約状態 */
const { data: teamInfo } = useTeamInfo();
const enableTechnicalLiterature = computed(
  () => teamInfo.value?.enable_technical_literature ?? false,
);
const enableUserDocument = computed(
  () => teamInfo.value?.enable_user_document ?? false,
);

/* ページのstate */
type PageState = 'loading' | 'invalidUrl' | 'loaded';
const pageState = ref<PageState>('loading');

/* 要約関連のstate */
const surveySessionId = ref<string>(route.params.survey_session_id as string); // surveysテーブルのuuid
const surveyHistories = ref<Array<SurveyHistoryData>>([]);
const latestSurveyHistory = computed(() => surveyHistories.value.slice(-1)[0]);
const question = computed({
  get: () => store.getters['survey/getSessionQuestion'](surveySessionId.value),
  set: (value: string) =>
    store.commit('survey/setSessionQuestion', {
      sessionId: surveySessionId.value,
      question: value,
    }),
});
const cached_expanded_queries = ref<string[]>([]); // クエリ変換・検索なし生成によってスレッドごとに（スレッド内でも実行タイミングによって文脈が変わり） 適切な文脈を保持した拡張クエリでないといけないため、要約では一旦拡張クエリのキャッシュはしない
const filterInfo = computed({
  get: () =>
    store.getters['survey/getSessionFilterInfo'](surveySessionId.value),
  set: (value: FilterInfo) =>
    store.commit('survey/setSessionFilterInfo', {
      sessionId: surveySessionId.value,
      filterInfo: value,
    }),
});
const deepMode = computed({
  get: () => store.getters['survey/getSessionDeepMode'](surveySessionId.value),
  set: (value: boolean) =>
    store.commit('survey/setSessionDeepMode', {
      sessionId: surveySessionId.value,
      deepMode: value,
    }),
});

// 許可されているdocTypesを取得
const allowedCurrentDocTypes = computed(() => {
  const docTypes = latestSurveyHistory.value?.filterInfo.docTypes ?? [];
  return docTypes.filter(docType => {
    switch (docType) {
      case 'patent':
      case 'research_paper':
        return enableTechnicalLiterature.value;
      case 'user_document':
        return enableUserDocument.value;
      default:
        return true;
    }
  });
});

// 計測で使用: 何のドキュメントを対象に要約（の中の検索）が行われたか
const docTypesToUse = computed(() => {
  return filterInfo.value.docTypes.length === 0
    ? DOC_TYPES_FOR_SCOPE(
        filterInfo.value.searchScope,
        enableTechnicalLiterature.value,
        enableUserDocument.value,
      )
    : filterInfo.value.docTypes;
});
const specifiedMethod = computed({
  get: () =>
    store.getters['survey/getSessionSpecifiedMethod'](surveySessionId.value),
  set: (value?: SpecifiedMethod) =>
    store.commit('survey/setSessionSpecifiedMethod', {
      sessionId: surveySessionId.value,
      specifiedMethod: value,
    }),
});
const executionFrom = computed({
  get: () =>
    store.getters['survey/getSessionExecutionFrom'](surveySessionId.value),
  set: (value?: SurveyExecutionFrom) =>
    store.commit('survey/setSessionExecutionFrom', {
      sessionId: surveySessionId.value,
      executionFrom: value,
    }),
});
const recommendedActionId = computed({
  get: () =>
    store.getters['survey/getSessionRecommendedActionId'](
      surveySessionId.value,
    ),
  set: (value?: number) =>
    store.commit('survey/setSessionRecommendedActionId', {
      sessionId: surveySessionId.value,
      recommendedActionId: value,
    }),
});
const surveyType = computed({
  get: () =>
    store.getters['survey/getSessionSurveyType'](surveySessionId.value),
  set: (value: SurveyType) =>
    store.commit('survey/setSessionSurveyType', {
      sessionId: surveySessionId.value,
      surveyType: value,
    }),
});
const shareSurveysKind = computed({
  get: () =>
    store.getters['survey/getSessionShareSurveysKind'](surveySessionId.value),
  set: (value: ShareSurveysKind | undefined) =>
    store.commit('survey/setSessionShareSurveysKind', {
      sessionId: surveySessionId.value,
      shareSurveysKind: value,
    }),
});
const shareSurveyLastRequestId = computed({
  get: () =>
    store.getters['survey/getSessionShareSurveyLastRequestId'](
      surveySessionId.value,
    ),
  set: (value: string | undefined) =>
    store.commit('survey/setSessionShareSurveyLastRequestId', {
      sessionId: surveySessionId.value,
      shareSurveyLastRequestId: value,
    }),
});
const shareSurveyPromptTypeName = computed({
  get: () =>
    store.getters['survey/getSessionShareSurveyPromptTypeName'](
      surveySessionId.value,
    ),
  set: (value: SurveyPromptType['name'] | undefined) =>
    store.commit('survey/setSessionShareSurveyPromptTypeName', {
      sessionId: surveySessionId.value,
      shareSurveyPromptTypeName: value,
    }),
});
const surveyPromptTemplate = computed({
  get: () =>
    store.getters['survey/getSurveyPromptTemplate'](surveySessionId.value),
  set: (value: SurveyPromptTemplateTrackingInfo | undefined) =>
    store.commit('survey/setSurveyPromptTemplate', {
      sessionId: surveySessionId.value,
      surveyPromptTemplate: value,
    }),
});

const enableVisasqNowQuestionDraftButton = computed(() => {
  if (!latestSurveyHistory.value) return false;
  return latestSurveyHistory.value.filterInfo.specifiedFiles.length === 0;
});

/* 表示制御に関するフラグstate */
const isLoaded = ref(false);
const isSurveyInProgress = computed(() => {
  return composableIsSurveyInProgress(surveySessionId.value);
});
const isFetchingHistories = ref<boolean>(false);
const isAnyVisasqDraftGenerating = computed(() => {
  return surveyHistories.value.some(history =>
    history.visasqNowQuestionDrafts.some(draft => draft.isGenerating),
  );
});
const canCreateShareSurvey = computed(() => {
  const firstHistory = surveyHistories.value[0];

  if (
    !firstHistory ||
    isSurveyInProgress.value ||
    isFetchingHistories.value ||
    isAnyVisasqDraftGenerating.value
  ) {
    return false;
  }

  const hasEnabledHistory = surveyHistories.value.some(history => {
    const isNormalHistoryExist =
      !history.deepMode && history.requestId !== '' && !history.status.isError;
    // ディープリサーチが成功しているものがあるかどうかを判断
    // 生成直後とそうでない場合と条件が異なる(requestStateがcompletedは生成直後)
    const isDeepResearchHistoryCompleted =
      history.deepMode &&
      (history.requestState === 'completed' ||
        (history.requestState === undefined && !history.incomplete));

    return isNormalHistoryExist || isDeepResearchHistoryCompleted;
  });

  return hasEnabledHistory;
});
const totalBlockCount = computed(() => {
  // surveyHistory数とビザスクnowの質問下書き数の合計をスレッド内に生成できる上限数とする
  return surveyHistories.value.reduce((total, history) => {
    return total + 1 + history.visasqNowQuestionDrafts.length;
  }, 0);
});
const bottomAnchor = ref<HTMLDivElement | null>(null); // スクロール用最下部アンカー

/* 関連質問の表示制御 */
const isRelatedQuestionsVisible = computed(() => {
  return (
    latestSurveyHistory.value?.relatedQuestions?.length > 0 &&
    totalBlockCount.value < SESSION_LIMIT_COUNT &&
    !isSurveyInProgress.value &&
    !isFetchingHistories.value &&
    !isAnyVisasqDraftGenerating.value
  );
});

/* ファイル指定履歴 */
const { createSpecifiedFileHistories } = useUserDocumentActionHistories(
  userSession.getUserId(),
);

/* stream API中断用コントローラ */
const generateController = ref<ControllerWithMode>({
  controller: null,
  deepMode: false,
});
const reconnectController = ref<ControllerWithMode>({
  controller: null,
  deepMode: false,
});

const {
  requestPollingRestart,
  isSurveyInProgress: composableIsSurveyInProgress,
} = useSurveyExecutionStatus({
  enablePolling: false,
});

/**
 * Handlers
 */

const startSurvey = async ({
  question: emitQuestion,
  filterInfo: emitFilterInfo,
  deepMode: emitDeepMode,
}: SurveyInputValue) => {
  question.value = emitQuestion;
  filterInfo.value = emitFilterInfo;
  deepMode.value = emitDeepMode;

  await submitSurveyStream(true);
};

const startSurveyFromRelatedQuestion = async (selectedQuestion: string) => {
  question.value = selectedQuestion;
  filterInfo.value = {
    searchScope: latestSurveyHistory.value?.filterInfo.searchScope ?? 'all',
    docTypes: allowedCurrentDocTypes.value,
    specifiedFiles: latestSurveyHistory.value?.filterInfo.specifiedFiles ?? [],
    groupId: latestSurveyHistory.value?.filterInfo.groupId,
    groupTags: latestSurveyHistory.value?.filterInfo.groupTags,
  };
  deepMode.value = false; // NOTE: 関連質問でディープリサーチを引き継いで実行するのは過剰なため、通常の要約とする

  await submitSurveyStream(true, undefined, true);
};

/**
 * ドキュメントの更新
 * `src/components/survey/document/survey-adp-document-card.vue`でマークを行うとemitで呼び出される
 *
 * @param {number} historyIndex - surveyHistoriesのindex
 * @param {number} sourceIndex - sourcesのindex
 * @param {AdpDocument} document - 更新するドキュメント
 */
const updateDocument = (
  historyIndex: number,
  sourceIndex: number,
  document: AdpDocument,
) => {
  const updatedSources = [...surveyHistories.value[historyIndex].sources];
  updatedSources[sourceIndex] = document;
  surveyHistories.value[historyIndex].sources = updatedSources;
};

/**
 * Surveyを実行 ( 「questionで検索を実行し、その結果をもとに要約を生成」を行うAPI呼び出しと、レスポンスのハンドリング )
 * @param {boolean} isNew - 新規surveyかどうか (v1以降の機能で、過去のquestionを編集して再度surveyを実行する想定があるため最初から対応)
 * @param {number} index - surveyHistoriesのindex (isNewがfalseの場合、過去のsurveyを編集するために使用する該当データのindex)
 */
const submitSurveyStream = async (
  isNew: boolean,
  index?: number,
  fromRelatedQuestion?: boolean,
) => {
  if (surveyHistories.value.length >= SESSION_LIMIT_COUNT) return;

  const sessionId = surveySessionId.value;
  const deepModeSnapshot = deepMode.value;
  const filterInfoSnapshot: FilterInfo = filterInfo.value;

  // 初期化
  if (isNew) {
    const latestIndex = surveyHistories.value.length - 1;
    if (surveyHistories.value[latestIndex]?.requestState) {
      surveyHistories.value[latestIndex].incomplete = true;
      surveyHistories.value[latestIndex].requestState = undefined;
    }

    surveyHistories.value.push({
      requestId: '',
      question: question.value,
      deepMode: deepModeSnapshot,
      filterInfo: filterInfoSnapshot,
      incomplete: false,
      sources: [],
      deepModeProcesses: [],
      originMarkdownReasoning: '',
      reasoning: [],
      originMarkdownSummary: '',
      summary: [],
      enableAnimation: true,
      feedbackType: undefined,
      relatedQuestions: [],
      visasqNowQuestionDrafts: [],
      status: {
        isInProgress: true,
        isNoHit: false,
        isError: false,
        errorCause: undefined,
        progressMessage: DEFAULT_SURVEY_PROGRESS_MESSAGE,
      },
    });
  }
  let surveyIndex = index ?? surveyHistories.value.length - 1;
  const rank = surveyIndex + 1;
  await scrollToBottomAnchor('smooth');

  // 計測
  trackSubmitSurvey(
    executionFrom.value,
    sessionId,
    rank,
    filterInfo.value,
    docTypesToUse.value,
    surveyType.value,
    deepModeSnapshot,
    rank > 1 ? surveyHistories.value[0].requestId : null,
    rank > 1 ? surveyHistories.value[surveyIndex - 1].requestId : null,
    specifiedMethod.value,
    fromRelatedQuestion,
    rank === 1 ? survey.value?.share_survey?.uuid : undefined,
    shareSurveyLastRequestId.value,
    shareSurveysKind.value,
    surveyPromptTemplate.value,
    isDefaultSurveyQuery(question.value, executionFrom.value),
    recommendedActionId.value,
  );
  executionFrom.value = 'surveys';
  recommendedActionId.value = undefined;
  specifiedMethod.value = undefined; // 深掘りの場合は、ファイル指定方法を引き継がない

  // ファイル指定履歴を追加
  if (filterInfoSnapshot.specifiedFiles.length > 0) {
    filterInfoSnapshot.specifiedFiles.forEach((file: { id: string }) => {
      createSpecifiedFileHistories(file.id);
    });
  }

  try {
    generateController.value = {
      controller: new AbortController(),
      deepMode: deepModeSnapshot,
    };

    requestPollingRestart();

    await SurveyGenerator.generate({
      sessionId,
      question: question.value,
      filterInfo: filterInfoSnapshot,
      cached_expanded_queries: cached_expanded_queries.value,
      deepMode: deepModeSnapshot,
      history: surveyHistories.value[surveyIndex],
      signal: generateController.value.controller?.signal,
    });
  } catch (e: unknown) {
    if (e instanceof Error && e.name === 'AbortError') return;

    surveyHistories.value[surveyIndex].status = {
      ...surveyHistories.value[surveyIndex].status,
      isInProgress: false,
      isError: true,
      errorCause: ((e as Error).cause as string) ?? undefined,
    };
    throw new Error(`要約生成に失敗しました: ${e as string}`);
  } finally {
    emitter.emit('update-survey-session', {
      session_id: sessionId,
      title: '',
      updated_at: new Date().toISOString(),
    });

    generateController.value = {
      controller: null,
      deepMode: false,
    };
  }
  // shareSurvey計測関連の値をリセット
  shareSurveyLastRequestId.value = undefined;
  shareSurveyPromptTypeName.value = undefined;
  surveyPromptTemplate.value = undefined;
  shareSurveysKind.value = undefined;
  surveyType.value = 'survey';
};

/**
 * 現在のsurvey_session_idに紐づくsurveyを取得
 */

const fetchSurvey = async (sessionId: string) => {
  pageState.value = 'loading';
  try {
    survey.value = await api.fetchSurvey(sessionId);
    pageState.value = 'loaded';
  } catch (error) {
    pageState.value = 'invalidUrl';
    survey.value = undefined;
    throw error;
  }
};

/**
 * スレッドに紐づく `survey_histories` と `survey_requests` のデータを取得
 */
const fetchSurveyStatus = async (sessionId: string) => {
  isFetchingHistories.value = true;

  const data = await api.fetchSurveyStatus(sessionId);
  const histories = data.histories;
  const requests = data.requests;

  const additionalSurveyHistories: Array<SurveyHistoryData> = [];

  if (histories.length > 0) {
    histories.forEach(history => {
      const drafts: Array<VisasqNowQuestionDraftData> = convertKeysToCamelCase(
        history.visasq_now_question_drafts,
        false,
      ).map((draft: VisasqNowQuestionDraftType) => ({
        ...draft,
        isGenerating: false,
        isError: false,
        enableAnimation: false,
      }));

      additionalSurveyHistories.push({
        requestId: history.request_id,
        question: history.question,
        deepMode: history.deep_mode,
        filterInfo: convertKeysToCamelCase(history.filter_info, false),
        incomplete: history.incomplete,
        sources: history.sources,
        deepModeProcesses: history.messages,
        originMarkdownReasoning: history.reasoning ?? '',
        reasoning: [],
        originMarkdownSummary: history.model_response ?? '',
        summary: [], // 画面初期表示のパースは`survey-history.vue`で行う
        enableAnimation: false,
        feedbackType: history.active_feedback?.evaluation,
        relatedQuestions: history.related_questions,
        visasqNowQuestionDrafts: drafts,
        status: {
          isInProgress: false,
          isNoHit: history.sources.length === 0 && !history.model_response,
          isError: false,
          errorCause: undefined,
          progressMessage: '',
        },
      });
    });
  }

  if (requests.length > 0) {
    const request = requests[0];

    additionalSurveyHistories.push({
      requestId: request.request_id,
      question: request.question,
      deepMode: request.deep_mode,
      filterInfo: convertKeysToCamelCase(request.filter_info, false),
      incomplete: false,
      sources: [],
      deepModeProcesses: [],
      originMarkdownReasoning: '',
      reasoning: [],
      originMarkdownSummary: '',
      summary: [],
      enableAnimation: true,
      feedbackType: undefined,
      relatedQuestions: [],
      visasqNowQuestionDrafts: [],
      requestState: request.state,
      status: {
        isInProgress: checkRequestState(
          request.state,
          ['stopping', 'stopped', 'aborted', 'completed'],
          false,
        ),
        isNoHit: false,
        isError: false,
        errorCause: undefined,
        progressMessage: DEFAULT_SURVEY_PROGRESS_MESSAGE,
      },
    });
  }

  surveyHistories.value = additionalSurveyHistories;

  // 生成途中 or 停止した surveyがある場合は、再接続する
  if (requests.length > 0) {
    const request = requests[0];
    const historyIndex = surveyHistories.value.length - 1;

    try {
      reconnectController.value = {
        controller: new AbortController(),
        deepMode: request.deep_mode,
      };
      await SurveyGenerator.reconnectSurveyStream({
        sessionId: sessionId,
        requestId: request.request_id,
        history: surveyHistories.value[historyIndex],
        signal: reconnectController.value.controller?.signal,
      });
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') return;

      surveyHistories.value[historyIndex].status = {
        ...surveyHistories.value[historyIndex].status,
        isError: true,
        errorCause: ((e as Error).cause as string) ?? undefined,
      };
      throw new Error(`要約再接続に失敗しました: ${e as string}`);
    } finally {
      reconnectController.value = {
        controller: null,
        deepMode: false,
      };
    }
  }

  isLoaded.value = true;
  isFetchingHistories.value = false;
};

/**
 * 新規でスレッドを作成した場合の、初回のsurvey自動実行
 */
const initializeSubmitSurvey = async () => {
  if (
    surveyHistories.value.length === 0 &&
    !isSurveyInProgress.value &&
    question.value
  ) {
    await submitSurveyStream(
      true,
      undefined,
      shareSurveyPromptTypeName.value === 'related_question',
    );
  }
};

/**
 * ビザスクnowの質問の叩きをLLMを使用して作成する
 */
const createVisasqNowQuestionDraft = async () => {
  const latestHistoryIndex = surveyHistories.value.length - 1;

  // まず空のオブジェクトを追加（ローディング表示用）
  const emptyDraft = {
    id: 0,
    title: '',
    background: '',
    question: '',
    sampleAnswer: '',
    createdAt: '',
    updatedAt: '',
    isGenerating: true,
    isError: false,
    enableAnimation: true,
  };

  const draftIndex =
    surveyHistories.value[latestHistoryIndex].visasqNowQuestionDrafts.push(
      emptyDraft,
    ) - 1;

  try {
    const draft = await api.visasqNowQuestionDraft(
      latestSurveyHistory.value?.requestId,
    );

    surveyHistories.value[latestHistoryIndex].visasqNowQuestionDrafts[
      draftIndex
    ] = {
      ...emptyDraft,
      ...convertKeysToCamelCase(draft, false),
      isGenerating: false,
    };

    // 計測
    try {
      await trackingVisasqNowEvent('visasq_now_request_generate', {
        from: 'survey_page',
        prompt_type: {
          name: 'visasq_now',
          original_request_id:
            surveyHistories.value[latestHistoryIndex].requestId,
        },
        original_sample_session_id: null,
        parent_request_id: surveyHistories.value[0].requestId,
        visasq_now_question_draft_id:
          surveyHistories.value[latestHistoryIndex].visasqNowQuestionDrafts[
            draftIndex
          ].id,
        session_id: surveySessionId.value,
      });
    } catch (trackingError) {
      // 計測エラーはビザスクnowのエラーにはしない
      // eslint-disable-next-line no-console
      console.error('trackingVisasqNowEvent error:', trackingError);
    }
  } catch (error) {
    surveyHistories.value[latestHistoryIndex].visasqNowQuestionDrafts[
      draftIndex
    ] = {
      ...emptyDraft,
      isGenerating: false,
      enableAnimation: false,
      isError: true,
    };
    throw new Error('ビザスクnowの質問ドラフトの生成に失敗しました: ' + error);
  }
};

/**
 * 画面最下部にスクロールする関数
 * @param behavior - scrollIntoViewメソッドのオプション
 */
const scrollToBottomAnchor = async (
  behavior: 'auto' | 'smooth' | 'instant',
) => {
  const normalizedBehavior = behavior === 'instant' ? 'auto' : behavior;
  await nextTick(() => {
    if (bottomAnchor.value) {
      bottomAnchor.value.scrollIntoView({ behavior: normalizedBehavior });
    }
  });
};

/**
 * 実行中のsurvey (stream) を中断
 *
 * NOTE: ディープリサーチ (deepMode = true) では、バックエンドでワーカーを使用しているためフロントエンド (クライアント) で中断して問題ないが、
 *       通常の要約 (deepMode = false) は、バックエンドでワーカーを使用しておらず、中断するとその時点の中途半端なデータが作成されてしまうため中断できない。
 */
function abortControllersIfDeepMode() {
  if (
    generateController.value?.deepMode ||
    reconnectController.value?.deepMode
  ) {
    generateController.value.controller?.abort();
    reconnectController.value.controller?.abort();
    generateController.value = {
      controller: null,
      deepMode: false,
    };
    reconnectController.value = {
      controller: null,
      deepMode: false,
    };
  }
}

const stopSubmitSurvey = async (requestId: string) => {
  try {
    await api.cancelSurvey(surveySessionId.value, requestId);

    const targetHistory = surveyHistories.value.find(
      h => h.requestId === requestId,
    );
    if (targetHistory) {
      targetHistory.requestState = 'stopping';
      targetHistory.status.isInProgress = false;
      targetHistory.status.progressMessage = '';
    }
  } catch (error) {
    throw new Error(
      `要約の停止に失敗しました: ${error instanceof Error ? error.message : error}`,
    );
  }
};

/**
 * 再生成
 */
const regenerateSurveyStream = async (history: SurveyHistoryData) => {
  // 対象のhistoryを初期化
  const targetIndex = surveyHistories.value.findIndex(
    h => h.requestId === history.requestId,
  );
  if (targetIndex !== -1) {
    surveyHistories.value[targetIndex] = {
      ...surveyHistories.value[targetIndex],
      sources: [],
      deepModeProcesses: [],
      originMarkdownReasoning: '',
      reasoning: [],
      originMarkdownSummary: '',
      summary: [],
      enableAnimation: true,
      feedbackType: undefined,
      relatedQuestions: [],
      visasqNowQuestionDrafts: [],
      requestState: undefined,
      status: {
        isInProgress: true,
        isNoHit: false,
        isError: false,
        errorCause: undefined,
        progressMessage: '',
      },
    };
  }

  try {
    generateController.value = {
      controller: new AbortController(),
      deepMode: history.deepMode,
    };

    requestPollingRestart();

    await SurveyGenerator.regenerate({
      sessionId: surveySessionId.value,
      requestId: history.requestId,
      history: surveyHistories.value[targetIndex],
      signal: generateController.value.controller?.signal,
    });
  } catch (e: unknown) {
    if (e instanceof Error && e.name === 'AbortError') return;

    surveyHistories.value[targetIndex].status = {
      ...surveyHistories.value[targetIndex].status,
      isInProgress: false,
      isError: true,
      errorCause: ((e as Error).cause as string) ?? undefined,
    };
    throw new Error(
      `再生成に失敗しました: ${e instanceof Error ? e.message : String(e)}`,
    );
  } finally {
    emitter.emit('update-survey-session', {
      session_id: surveySessionId.value,
      title: '',
      updated_at: new Date().toISOString(),
    });

    executionFrom.value = 'surveys';
    specifiedMethod.value = undefined; // 深掘りの場合は、ファイル指定方法を引き継がない
    generateController.value = {
      controller: null,
      deepMode: false,
    };
  }
};

const disableAnimation = (targetIndex: number) => {
  const targetHistory = surveyHistories.value[targetIndex];
  if (targetHistory?.enableAnimation) targetHistory.enableAnimation = false;
};

/**
 * 高速なスレッド切り替え時の並列実行を検知する関数
 * @param currentSessionId - 現在のsurvey_session_id
 * @param expectedSessionId - 比較するsurvey_session_id
 * @returns セッションIDが変更されているかどうか
 */
const isSessionIdChanged = (
  currentSessionId: string,
  expectedSessionId: string,
): boolean => {
  return currentSessionId !== expectedSessionId;
};

provide(specifiedMethodKey, specifiedMethod);

onMounted(async () => {
  executionFrom.value = executionFrom.value ?? 'surveys';
  await store.dispatch('survey/initSession', surveySessionId.value);
  await Promise.all([
    fetchSurveyStatus(surveySessionId.value),
    fetchSurvey(surveySessionId.value),
  ]);
  await nextTick();
  await scrollToBottomAnchor('instant');
  trackSurveyPageView(
    SURVEY_HISTORIES_PAGE_NAME,
    getCurrentPathForTracking(),
    surveySessionId.value,
  );
  await initializeSubmitSurvey();
});

// NOTE: 現状のAnewsの作りではパスが同じでパラメータだけ違う場合に、再レンダリングが発生しないため、自力で検知して初期表示処理を行う
// 例) 別の履歴を選択して要約結果を表示するようなパターンで、`/survey/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx` から `/survey/yyyyyyyy-yyyy-yyyy-yyyy-yyyyyyyyyyyy` に遷移した時
onBeforeRouteUpdate(async (to, from, next) => {
  if (to.params.survey_session_id !== from.params.survey_session_id) {
    abortControllersIfDeepMode();
    const prevSessionId = from.params.survey_session_id as string;
    const newSessionId = to.params.survey_session_id as string;

    // 遷移前のスレッドのストアのstateをクリーンアップ（手動）
    if (prevSessionId && store.getters['survey/hasSession'](prevSessionId)) {
      await store.dispatch('survey/removeSession', prevSessionId);
    }
    // 遷移先のスレッドのストアのstateを初期化
    await store.dispatch('survey/initSession', newSessionId);

    surveySessionId.value = newSessionId;
    surveyHistories.value = [];
    survey.value = undefined;

    if (isSessionIdChanged(surveySessionId.value, newSessionId)) return;

    next();

    try {
      if (isSessionIdChanged(surveySessionId.value, newSessionId)) return;

      await Promise.all([
        fetchSurveyStatus(surveySessionId.value),
        fetchSurvey(surveySessionId.value),
      ]);
      await nextTick();
      await scrollToBottomAnchor('instant');
      trackSurveyPageView(
        SURVEY_HISTORIES_PAGE_NAME,
        getCurrentPathForTracking(),
        surveySessionId.value,
      );

      if (isSessionIdChanged(surveySessionId.value, newSessionId)) return;

      await initializeSubmitSurvey();
    } catch (e) {
      if ((e as Error).name === 'AbortError') next();
    }
  }
});

onUnmounted(async () => {
  abortControllersIfDeepMode();

  // 古いスレッドのストアのstateを一気に削除
  await store.dispatch('survey/cleanupSessions');
});
</script>

<template>
  <template v-if="pageState === 'loaded'">
    <div class="survey-session-container">
      <SurveyHeader
        v-if="survey"
        :title="survey.title"
        :user-name="userInfo?.user_name ?? ''"
        :user-icon-url="userInfo?.image_url"
        :updated-at="formatDate(survey.updated_at)"
        :session-id="surveySessionId"
        :can-create-share-survey="canCreateShareSurvey"
      />
      <div class="content-wrapper">
        <router-link
          v-if="survey?.has_parent_share_survey && survey.share_survey"
          :to="{
            name: 'shareSurvey',
            params: { uuid: survey.share_survey.uuid },
          }"
        >
          <SurveyCard
            class="share-survey-card"
            :key="survey.share_survey.uuid"
            :highlight-keywords="[]"
            :title="survey.share_survey.title"
            :content="survey.share_survey.content || ''"
            :user-name="survey.share_survey.owner.user_name"
            :user-icon-url="survey.share_survey.owner.image_url || ''"
            :use-anews-icon="!survey.share_survey.owner.image_url"
            :updated-at="formatDate(survey.share_survey.updated_at)"
          >
            <template #header>
              <div class="share-survey-card-header">
                <DgrIcon
                  size="small"
                  :keep-fill="false"
                  name="share-thread"
                  class="icon"
                />
                <div>作成元のシェアスレッド</div>
              </div>
            </template>
          </SurveyCard>
        </router-link>
        <div
          v-else-if="survey?.has_parent_share_survey && !survey.share_survey"
          class="forbidden-share-survey"
        >
          <div class="share-survey-card-header">
            <DgrIcon
              size="small"
              :keep-fill="false"
              name="share-thread"
              class="icon"
            />
            <div>作成元のシェアスレッド</div>
          </div>
          <div class="c-text-regular--m">アクセスが無効になっています</div>
        </div>
        <div
          class="content"
          v-for="(history, index) in surveyHistories"
          :key="index"
          :class="`survey-history-${index}`"
        >
          <SurveyHistory
            :session-id="surveySessionId"
            :request-id="history.requestId"
            :question="history.question"
            :incomplete="history.incomplete"
            :sources="history.sources"
            :deep-mode-processes="history.deepModeProcesses"
            :origin-markdown-reasoning="history.originMarkdownReasoning"
            :reasoning="history.reasoning"
            :origin-markdown-summary="history.originMarkdownSummary"
            :summary="history.summary"
            :enable-animation="history.enableAnimation"
            :enable-feedback="true"
            enable-source-tab
            :feedback-type="history.feedbackType"
            :filter-info="history.filterInfo"
            :status="history.status"
            :deep-mode="history.deepMode"
            :request-state="history.requestState"
            :rank="index + 1"
            :page-name="SURVEY_HISTORIES_PAGE_NAME"
            @update-document="
              ({ sourceIndex, document }) =>
                updateDocument(index, sourceIndex, document)
            "
            @stop="stopSubmitSurvey(history.requestId)"
            @regenerate="regenerateSurveyStream(history)"
            @disable-animation="disableAnimation(index)"
          />
          <VisasqNowQuestionDraft
            v-for="(
              visasqNowQuestionDraft, idx
            ) in history.visasqNowQuestionDrafts"
            :key="`history-${index}-visasq-now-${idx}`"
            :enable-animation="visasqNowQuestionDraft.enableAnimation"
            :is-generating="visasqNowQuestionDraft.isGenerating"
            :is-error="visasqNowQuestionDraft.isError"
            :survey-history-question="history.question"
            :title="visasqNowQuestionDraft.title"
            :background="visasqNowQuestionDraft.background"
            :question="visasqNowQuestionDraft.question"
            :sample-answer="visasqNowQuestionDraft.sampleAnswer"
            :id="visasqNowQuestionDraft.id"
            :session-id="surveySessionId"
          />
        </div>
        <SurveyRelatedQuestions
          v-if="isRelatedQuestionsVisible"
          :related-questions="latestSurveyHistory.relatedQuestions"
          :enable-visasq-now-button="enableVisasqNowQuestionDraftButton"
          @select-question="startSurveyFromRelatedQuestion"
          @select-visasq-now="createVisasqNowQuestionDraft"
        />
        <div
          ref="bottomAnchor"
          class="scroll-bottom-anchor"
          aria-hidden="true"
        ></div>
        <div class="input-wrapper">
          <SurveyInput
            :key="surveySessionId"
            v-if="totalBlockCount < SESSION_LIMIT_COUNT"
            :session-id="surveySessionId"
            :enable-transform-input="true"
            :is-loading="isSurveyInProgress || isFetchingHistories"
            :initial-search-scope="latestSurveyHistory?.filterInfo.searchScope"
            :initial-doc-types="allowedCurrentDocTypes"
            :initial-specified-files="
              latestSurveyHistory?.filterInfo.specifiedFiles
            "
            :initial-group-id="latestSurveyHistory?.filterInfo.groupId"
            :initial-group-tag-ids="latestSurveyHistory?.filterInfo?.groupTags"
            :page-name="SURVEY_HISTORIES_PAGE_NAME"
            @submit="startSurvey"
          />
          <SurveySessionSwitcherInput v-else />
        </div>
      </div>
    </div>
  </template>
  <template v-else-if="pageState === 'invalidUrl'">
    <div class="invalid-url-message">無効なURLです</div>
  </template>
</template>

<style lang="scss" scoped>
.survey-session-container {
  width: 100%;

  .content-wrapper {
    max-width: var(--survey-session-width);
    margin: 0 auto;
    margin-bottom: 72px; // 入力欄 (survey-input.vue) にフォーカスが当たっている時に、下の方の文字が見えないので余白をとる
    padding: 0 32px;
  }

  .content {
    margin: 40px 0;
  }

  .scroll-bottom-anchor {
    width: 0;
    height: 0;
    overflow: hidden;
    position: absolute;
    left: -9999px;
  }
  .share-survey-card {
    margin-top: 16px;
  }
  .share-survey-card-header {
    font-size: 12px;
    color: $color-gray800;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    .icon {
      color: $color-gray800;
    }
  }
  .forbidden-share-survey {
    margin-top: 16px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-radius: $border-radius;
    background-color: $color-white;
    border: 1px solid $color-gray400;
  }
}
.invalid-url-message {
  padding: 8px 24px;
}
</style>
